// #143124: a dropdown panel that is exactly as wide as its trigger truncates
// every option that is longer than the trigger — which is what happens as soon
// as the widget is translated ("Meiste Stimmen" instead of "Most voted"). The
// panel is therefore sized to its content and only anchored to the trigger, so
// it may grow past the trigger's edges. That freedom needs a bound: the widget
// is a narrow iframe, and a content-wide panel next to a right-hand trigger
// would otherwise run out of it.

export type DropdownAlign = "start" | "end";

export interface DropdownBounds {
  left: number;
  right: number;
}

export interface DropdownPlacementInput {
  /** Trigger bounds relative to the viewport (getBoundingClientRect). */
  triggerLeft: number;
  triggerRight: number;
  /** Natural (unclamped) width of the panel. */
  contentWidth: number;
  /** The box the panel must stay inside, viewport-relative. */
  bounds: DropdownBounds;
  /** Gap kept between the panel and the edges of that box. */
  margin?: number;
}

export interface DropdownPlacement {
  align: DropdownAlign;
  maxWidth: number;
}

/**
 * Decides which trigger edge the panel hangs off and how wide it may grow.
 *
 * The panel is left-aligned by default (it reads as an extension of the
 * trigger). It flips to the trigger's right edge only when that genuinely buys
 * room for the content — never when left-aligning already fits.
 */
export const resolveDropdownPlacement = ({
  triggerLeft,
  triggerRight,
  contentWidth,
  bounds,
  margin = 8,
}: DropdownPlacementInput): DropdownPlacement => {
  // Growing rightwards from the trigger's left edge vs. leftwards from its right edge.
  const spaceFromStart = Math.max(0, bounds.right - triggerLeft - margin);
  const spaceFromEnd = Math.max(0, triggerRight - bounds.left - margin);

  const align: DropdownAlign =
    contentWidth <= spaceFromStart || spaceFromStart >= spaceFromEnd
      ? "start"
      : "end";

  return {
    align,
    // The second bound matters when the trigger itself is (partly) scrolled out
    // of the box: the space on its roomier side is then measured past an edge,
    // and without this the panel could end up wider than the box.
    maxWidth: Math.min(
      align === "start" ? spaceFromStart : spaceFromEnd,
      Math.max(0, bounds.right - bounds.left - margin * 2),
    ),
  };
};

/**
 * The box an absolutely positioned popover cannot escape: the nearest ancestor
 * that clips or scrolls horizontally (the roadmap's own `overflow: auto` list),
 * falling back to the widget viewport.
 */
export const getHorizontalClipBounds = (
  element: HTMLElement | null,
): DropdownBounds => {
  let current = element?.parentElement ?? null;

  while (current && current !== document.body) {
    if (window.getComputedStyle(current).overflowX !== "visible") {
      const rect = current.getBoundingClientRect();
      return { left: rect.left, right: rect.right };
    }
    current = current.parentElement;
  }

  return { left: 0, right: document.documentElement.clientWidth };
};
